"use client";

import React, { useEffect, useState } from "react";
import { Leaf, Tractor, Wheat } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface AnimatedCounterProps {
  end: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
}
function AnimatedCounter({
  end,
  prefix = "",
  suffix = "",
  duration = 2000
}: AnimatedCounterProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.5
  });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let startTime: number | null = null;
    let animationFrameId: number;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const percentage = Math.min(progress / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - percentage, 4);
      setCount(Math.floor(easeOutQuart * end));
      if (percentage < 1) {
        animationFrameId = requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };
    animationFrameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrameId);
  }, [isVisible, end, duration]);
  return <span ref={ref} className="text-6xl md:text-7xl lg:text-8xl font-default font-bold tracking-tighter text-primary drop-shadow-[0_0_15px_rgba(0,255,255,0.3)]">
      {prefix}{count}{suffix}
    </span>;
}
const statsData = [{
  icon: Leaf,
  value: 98,
  suffix: "%",
  label: "Cost Reduction",
  description: "Our AI agents eliminate the overhead of traditional IT departments, delivering massive savings.",
  color: "text-emerald-500"
}, {
  icon: Tractor,
  value: 2,
  prefix: "< ",
  suffix: "m",
  label: "Resolution Time",
  description: "Complex support cases identified and resolved in minutes, not hours or days.",
  color: "text-cyan-400"
}, {
  icon: Wheat,
  value: 100,
  prefix: "∞",
  suffix: "",
  label: "Scalability",
  description: "Handle infinite concurrent tickets without increasing headcount or compromising quality.",
  color: "text-white"
}];
export default function TheEfficiencyGap() {
  const {
    ref: narrativeRef,
    isVisible: isNarrativeVisible
  } = useRevealOnScroll({
    once: true
  });
  return <section data-section-id="223882" id="the-efficiency-gap" className="bg-background text-foreground py-24 md:py-32 overflow-hidden">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        
        {}
        <div ref={narrativeRef} className={`max-w-3xl mx-auto text-center transition-all duration-1000 transform ${isNarrativeVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-sm font-default font-semibold tracking-widest uppercase text-primary mb-4">
            Performance Metrics
          </h2>
          <h3 className="text-4xl md:text-5xl font-default font-bold mb-6 leading-tight tracking-tight">
            The Efficiency Gap
          </h3>
          <p className="text-lg md:text-xl text-muted-foreground font-mono leading-relaxed">
            Traditional IT support is limited by human bandwidth. AI Tech Solutions bridges the gap with autonomous agents that operate at machine speed with surgical precision.
          </p>
        </div>

        {}
        <div className="flex justify-center my-16 md:my-24">
          <Separator className="w-24 bg-primary/20 h-[1px]" />
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 lg:gap-16">
          {statsData.map((stat, i) => <div key={i} data-index={i} className="flex flex-col items-center text-center group">
              <div className={`mb-8 p-4 rounded-lg bg-secondary/50 ${stat.color} transition-all duration-500 group-hover:shadow-[0_0_20px_rgba(0,255,255,0.15)] border border-white/5`}>
                <stat.icon size={40} strokeWidth={1.5} />
              </div>
              
              <div className={`mb-4 h-24 flex items-end justify-center ${stat.color}`}>
                <AnimatedCounter end={stat.value} prefix={stat.prefix} suffix={stat.suffix} />
              </div>
              
              <h4 className="text-xl font-default font-bold mb-3 tracking-tight">
                {stat.label}
              </h4>
              
              <div className="w-full max-w-[200px] h-1 bg-muted rounded-md mb-4 overflow-hidden">
                <div className={`h-full ${stat.color === 'text-white' ? 'bg-white' : stat.color.replace('text-', 'bg-')} transition-all duration-1000 delay-500`} style={{
              width: isNarrativeVisible ? '100%' : '0%'
            }} />
              </div>
              
              <p className="text-muted-foreground font-sans text-sm md:text-base leading-relaxed max-w-[280px]">
                {stat.description}
              </p>
            </div>)}
        </div>

      </div>
    </section>;
}